import { useEffect, useState } from 'react';
import { Loader2, Check, ListChecks } from 'lucide-react';
import { useGetCsChecklistQuery, useUpdateCsChecklistMutation } from '@/store/api/apiSlice';
import type { CsChecklist } from '@/types';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

interface CsChecklistPanelProps {
    investigationId: string;
    // CS owners toggle steps; Super Admin views read-only ("Super Admin can read it").
    canEdit?: boolean;
}

// Compute progress locally (sections-done / total-sections) so the bar updates
// instantly on an optimistic toggle, before the server's numbers land.
function computeProgress(checklist: CsChecklist): { checked: number; total: number; percent: number } {
    const total = checklist.sections.length;
    const checked = checklist.sections.filter((s) => s.checked).length;
    const percent = total > 0 ? Math.round((checked / total) * 100) : 0;
    return { checked, total, percent };
}

export default function CsChecklistPanel({ investigationId, canEdit = false }: CsChecklistPanelProps) {
    const { data, isLoading, isError, error } = useGetCsChecklistQuery(investigationId, { skip: !investigationId });
    const [updateChecklist] = useUpdateCsChecklistMutation();

    // Local copy so checkboxes feel instant; replaced by the authoritative response.
    const [checklist, setChecklist] = useState<CsChecklist | null>(null);
    const [savingItem, setSavingItem] = useState<string | null>(null);
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        if (data) setChecklist(data);
    }, [data]);

    const handleToggle = async (sectionId: string, nextChecked: boolean) => {
        if (!canEdit || !checklist || savingItem) return;
        setSaveError('');

        // Optimistic local update — flip the section, recompute progress.
        const optimistic: CsChecklist = {
            ...checklist,
            sections: checklist.sections.map((section) =>
                section.id === sectionId ? { ...section, checked: nextChecked } : section,
            ),
        };
        optimistic.progress = computeProgress(optimistic);
        setChecklist(optimistic);
        setSavingItem(sectionId);

        try {
            const refreshed = await updateChecklist({
                id: investigationId,
                sections: [{ sectionId, checked: nextChecked }],
            }).unwrap();
            setChecklist(refreshed);
        } catch (err) {
            // Revert to the last server-known state.
            setChecklist(data ?? null);
            setSaveError(parseErrorMessage(err, 'Could not save. Please try again.'));
        } finally {
            setSavingItem(null);
        }
    };

    if (isLoading) {
        return (
            <div className="rounded-2xl border border-gray-100 p-5">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading checklist…
                </div>
            </div>
        );
    }

    if (isError || !checklist) {
        return (
            <div className="rounded-2xl border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <ListChecks className="h-4 w-4 text-[#FF6B35]" /> Progress Checklist
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                    {parseErrorMessage(error, 'Checklist is not available for this investigation.')}
                </p>
            </div>
        );
    }

    const { progress } = checklist;

    return (
        <div className="rounded-2xl border border-gray-100 p-5">
            {/* Header + progress */}
            <div className="mb-4">
                <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        <ListChecks className="h-4 w-4 text-[#FF6B35]" /> Progress Checklist
                    </h3>
                    <span className="text-xs font-medium text-gray-500">
                        {progress.checked}/{progress.total} done
                    </span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                    <div
                        className="h-full rounded-full bg-[#FF6B35] transition-all"
                        style={{ width: `${progress.percent}%` }}
                    />
                </div>
                {!canEdit && (
                    <p className="mt-2 text-[11px] text-gray-400">
                        Read-only maintained by the Customer Success owner.
                    </p>
                )}
            </div>

            {saveError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">{saveError}</p>}

            {/* Sections — one checkbox per section; items are read-only context. */}
            <div className="flex flex-col gap-4">
                {checklist.sections.map((section) => {
                    const isSaving = savingItem === section.id;
                    return (
                        <div key={section.id} className="rounded-xl border border-gray-100 p-3">
                            <button
                                type="button"
                                disabled={!canEdit || !!savingItem}
                                onClick={() => handleToggle(section.id, !section.checked)}
                                className={`flex w-full items-start gap-2.5 text-left ${
                                    canEdit ? 'cursor-pointer' : 'cursor-default'
                                }`}
                            >
                                <span
                                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                        section.checked
                                            ? 'border-[#FF6B35] bg-[#FF6B35] text-white'
                                            : 'border-gray-300 bg-white'
                                    }`}
                                >
                                    {isSaving ? (
                                        <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
                                    ) : section.checked ? (
                                        <Check className="h-3.5 w-3.5" />
                                    ) : null}
                                </span>
                                <span
                                    className={`text-sm font-semibold leading-snug ${
                                        section.checked ? 'text-gray-500' : 'text-gray-900'
                                    }`}
                                >
                                    {section.title}
                                </span>
                            </button>

                            {section.items.length > 0 && (
                                <ul className="mt-2 flex flex-col gap-1 pl-[30px]">
                                    {section.items.map((item) => (
                                        <li key={item.id} className="flex items-start gap-1.5 text-xs text-gray-500">
                                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-300" />
                                            <span className="leading-snug">{item.question}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
